/**
 * Shape of the `rovoConfig` field on `HelpCenterPage`, as selected by `hubQuery`.
 *
 * Typed structurally rather than off the generated Relay type so this stays a plain pure function
 * with no codegen dependency. The field is absent entirely when `hubs_rovo_config_from_kosh` is off
 * (`@include(if:)`), hence `undefined` is a valid input.
 */
type KoshRovoConfig =
	| {
			readonly agentId?: string | null;
			readonly isCustomAgentsAvailable?: boolean | null;
			readonly isRovoEnabled?: boolean | null;
	  }
	| null
	| undefined;

/**
 * Config consumed by `RovoWidgetConfigProvider`.
 */
type RovoWidgetConfig = {
	agentId: string;
};

/**
 * Derives the Rovo widget config from help-kosh's `HelpCenterPage.rovoConfig`.
 *
 * Deliberately mirrors the checks in `RovoWidgetProviderInner` so both data sources agree: Rovo must
 * be available for the site AND the admin widget toggle on AND an agent linked. Anything short of
 * that yields null, which the callout banner treats as a definitive "no Rovo here" — with
 * `hubs_rovo_config_from_kosh` on it does not re-ask via `rovoWidgetProviderQuery`.
 *
 * Returns null for all of:
 *  - the gate being off (field absent, so `undefined`)
 *  - help-kosh not serving it (null value: its own server gate off, a caller convo-ai won't serve,
 *    or any upstream failure — help-kosh maps every one of those to null rather than to a GraphQL
 *    error, so a failure fetching a decorative banner's config cannot blank the entire hub page)
 *  - a genuine "no Rovo" answer (`isCustomAgentsAvailable` false)
 *  - the widget existing but disabled by an admin (`isRovoEnabled` false)
 *  - no agent linked to the widget (`agentId` null)
 *
 * The result is hub-scoped, not page-scoped: help-kosh resolves it from the page's `helpCenterAri`,
 * so every page of a hub (CUSTOM_HOME, CUSTOM and PORTAL) gets the same answer.
 */
export const deriveRovoWidgetConfig = (rovoConfig: KoshRovoConfig): RovoWidgetConfig | null => {
	if (!rovoConfig?.isCustomAgentsAvailable || !rovoConfig.isRovoEnabled || !rovoConfig.agentId) {
		return null;
	}

	return { agentId: rovoConfig.agentId };
};
